import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Job, actions } from '@kanda-libs/ks-frontend-services';
import { AsyncThunkActionError } from '@kanda-libs/ks-frontend-services/src/store/types';
import type { StringIndexedObject } from '@kanda-libs/ks-frontend-services/src/types';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import useApiError from 'hooks/useApiError';
import { selectors } from 'store';
import type { FormattedJob } from 'types';

interface ReapplyJobHook {
  isSubmitting: boolean;
  initialReapply: (job: Job | FormattedJob) => void;
  error?: StringIndexedObject | string;
}

const shouldJobReapply = (job: Job | FormattedJob) => {
  if (!job) return false;
  if (!job.customer_options) return false;
  return job.status === 'sent' && job.finance_status === 'finance_not_approved';
};

/**
 * Returns current job and loading state
 */
const useReapplyJob = (): ReapplyJobHook => {
  const dispatch = useAppDispatch();
  const isSubmitting = useSelector(selectors.job.getIsSubmitting);

  const { push } = useHistory();
  const onError = useApiError();

  const [error, setError] = useState<AsyncThunkActionError | undefined>();

  const initialReapply = useCallback(
    (job: Job | FormattedJob) => {
      const shouldReapply = shouldJobReapply(job);
      const { id } = job;
      if (!shouldReapply || !id) return;
      dispatch(
        actions.reapplyJob({
          body: {},
          params: { id },
          forceReload: true,
          onSuccess: () => {
            push(URLS.reapply.replace(':id?', id));
          },
          onError: (currentError) => {
            onError(currentError);
            setError(currentError);
          },
        }),
      );
    },
    [push, onError, dispatch],
  );

  return {
    initialReapply,
    isSubmitting,
    error,
  };
};

export default useReapplyJob;
